import { motion } from "framer-motion";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { Target, Eye, ShieldCheck, Handshake } from "lucide-react";

const CHAPTERS = [
  {
    no: "01",
    title: "Doğduğumuz Yer: Samandağ",
    text: "Hatay'ın bereketli topraklarında, güneşin en cömert olduğu bir coğrafyada kurulduk. Bölgemizin enerji potansiyelini mühendislikle buluşturmak için yola çıktık.",
  },
  {
    no: "02",
    title: "Mühendislik Önce Gelir",
    text: "Her projeye güneşlenme analizi, çatı statiği ve şebeke uygunluğu ile başlarız. Tahmin değil, hesap yaparız. Böylece taahhüt ettiğimiz verimi gerçekte de görürsünüz.",
  },
  {
    no: "03",
    title: "Anahtar Teslim Sorumluluk",
    text: "Fizibilite, ruhsat, teşvik başvurusu, kurulum, şebeke bağlantısı ve devreye alma; tüm süreci tek elden yönetiriz. Siz sadece üretmeye ve tasarruf etmeye odaklanın.",
  },
  {
    no: "04",
    title: "25 Yıllık Bir Söz",
    text: "Bir GES yatırımı çeyrek asırlık bir ilişkidir. Kurduğumuz her sistemin ömrü boyunca yanınızda oluyor, izleme ve bakım hizmetiyle verimi koruyoruz.",
  },
];

const VALUES = [
  { icon: Target, title: "Verimlilik", text: "Her m²'den maksimum kazanç." },
  { icon: ShieldCheck, title: "Güvenilirlik", text: "Standartlara tam uyum, garantili işçilik." },
  { icon: Eye, title: "Şeffaflık", text: "Net fizibilite, gizli maliyet yok." },
  { icon: Handshake, title: "Süreklilik", text: "Kurulum sonrası kesintisiz destek." },
];

export default function About() {
  return (
    <>
      <PageHeader
        eyebrow="Hakkımızda"
        titleLines={["Güneşi mühendislikle", "buluşturuyoruz."]}
        description="Renewolt Enerji | Mühendislik; sürdürülebilir enerjiyi herkes için erişilebilir kılmak amacıyla kurulmuş, mühendislik odaklı bir güneş enerjisi şirketidir."
      />

      {/* Manifesto chapters */}
      <section data-testid="about-manifesto" className="relative bg-[#0A0A0A] py-16 sm:py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 space-y-16 sm:space-y-24">
          {CHAPTERS.map((c, i) => (
            <div key={c.no} className={`grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-start ${i % 2 === 1 ? "lg:pl-[16%]" : ""}`}>
              <Reveal className="lg:col-span-3">
                <div className="font-display font-black text-7xl sm:text-8xl tracking-tighter text-transparent [-webkit-text-stroke:1px_#5CC528]">
                  {c.no}
                </div>
              </Reveal>
              <div className="lg:col-span-9 max-w-2xl">
                <Reveal delay={0.1}>
                  <h2 className="font-display font-light text-3xl sm:text-4xl tracking-tight text-white mb-4">{c.title}</h2>
                </Reveal>
                <Reveal delay={0.2}>
                  <p className="text-neutral-400 text-lg leading-relaxed">{c.text}</p>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Image band */}
      <section className="relative h-[50vh] min-h-[360px] overflow-hidden grain">
        <motion.img
          initial={{ scale: 1.15 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          src="https://images.pexels.com/photos/35726122/pexels-photo-35726122.jpeg"
          alt="Güneş enerji santrali"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/40 to-[#0A0A0A]/60" />
        <div className="relative h-full flex items-center justify-center px-5">
          <Reveal>
            <p className="font-display font-light text-3xl sm:text-5xl tracking-tight text-white text-center max-w-3xl leading-tight">
              "Temiz enerji bir tercih değil, <span className="text-[#5CC528]">geleceğin standardıdır.</span>"
            </p>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="relative bg-[#0A0A0A] py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tighter text-white mb-14">Değerlerimiz</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={i * 0.08}>
                  <div className="h-full p-8 rounded-2xl bg-[#121212] border border-white/10 hover:border-[#5CC528]/40 transition-colors duration-300">
                    <Icon size={26} className="text-[#5CC528] mb-6" />
                    <h3 className="font-display font-semibold text-xl text-white mb-2">{v.title}</h3>
                    <p className="text-sm text-neutral-400 leading-relaxed">{v.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
